import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiChevronRight,
  FiFileText,
  FiImage,
  FiLock,
  FiMapPin,
  FiPackage,
  FiShield,
  FiTrash2,
  FiUploadCloud,
  FiVideo,
  FiDollarSign,
  FiInfo,
} from "react-icons/fi";
import toast from "react-hot-toast";
import axios from "../../Utils/axiosInstance";
import { s3Upload } from "../../Utils/s3Upload";
import OTPModal from "../../Components/UserSideComponents/RequirementsPost/Components/OTPModal";
import AppLoader from "../../Components/Common/AppLoader";
import { useAuth } from "../../Components/Context/AuthContext";
import { useWallet } from "../../Components/Context/WalletContext";

const PLATFORM_FEE = 500;

const RQM_DECLARATION_POINTS = [
  "I confirm that the product variety, quantity, and price range submitted in this RQM request are factual and relevant to the current requirement.",
  "I understand that PCM Exports shall review this request before activation and that the request will be published only after successful eligibility checks.",
  "I agree that the ₹500 platform fee is applicable for posting this RQM request and can be debited from my wallet upon final submission.",
  "I accept that this request is governed by the platform terms and that inaccurate or misleading content may lead to rejection or penalty action.",
];

const RQM_HOW_TO_STEPS = [
  {
    title: "Select Product Variety",
    desc: "Choose category, sub-category, product, and variety.",
  },
  {
    title: "Quantity & Price Details",
    desc: "Fill MOQ, offered rate, Minimum Pack Weight, dimensions & pickup location.",
  },
  {
    title: "Upload Reference Media",
    desc: "Add 3 compulsory images (2 Product, 1 Packaging) & 2 videos.",
  },
  {
    title: "Submit & Activate RQM",
    desc: "Accept declaration, verify OTP, and pay ₹500 platform fee.",
  },
];

const PostRQMRequest = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { refreshWallet, walletBalance } = useWallet();

  const paramCatId = searchParams.get("category");
  const paramSubId = searchParams.get("subCategory");
  const paramProdId = searchParams.get("product");
  const paramVarId = searchParams.get("variety");

  // Steps: 0 = Selection, 1 = Quantity & Price, 2 = Reference Media
  const [currentStep, setCurrentStep] = useState(0);

  // Scroll to top of page when changing steps
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  // Master Data
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productVarieties, setProductVarieties] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Selections
  const [category, setCategory] = useState(null);
  const [subCategory, setSubCategory] = useState(null);
  const [product, setProduct] = useState(null);
  const [variety, setVariety] = useState(null);

  const [quantity, setQuantity] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [packSize, setPackSize] = useState({
    weight: "",
    unit: "kg",
    dimensions: { measured: "cm", length: "", breadth: "", height: "" },
  });
  const [customDimensionUnit, setCustomDimensionUnit] = useState("");
  const [isCustomDimensionUnit, setIsCustomDimensionUnit] = useState(false);
  const [holdingPeriod, setHoldingPeriod] = useState({ from: "", to: "" });

  // Addresses
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressesLoading, setAddressesLoading] = useState(false);

  // Reference Media
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [specification, setSpecification] = useState("");

  // Modals & Process States
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState("");
  const [showDeclarationModal, setShowDeclarationModal] = useState(false);
  const [isDeclarationAccepted, setIsDeclarationAccepted] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  // Fetch Master Data & Addresses
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        setDataLoading(true);
        const [catRes, subRes, prodRes, varRes] = await Promise.all([
          axios.get("/category/get-all?special=all"),
          axios.get("/sub-category/get-all"),
          axios.get("/product/get-all"),
          axios.get("/product-variety/get-all"),
        ]);
        setCategories(catRes?.data?.data || catRes?.data || []);
        setSubCategories(subRes?.data?.data || subRes?.data || []);
        setProducts(prodRes?.data?.data || prodRes?.data || []);
        setProductVarieties(varRes?.data?.data || varRes?.data || []);
      } catch (err) {
        console.error("Master data fetch error:", err);
        toast.error("Failed to load catalog options.");
      } finally {
        setDataLoading(false);
      }
    };

    const fetchUserAddresses = async () => {
      try {
        setAddressesLoading(true);
        const res = await axios.get("/address");
        const list = res?.data?.data || [];
        setAddresses(list);
        const def = list.find((a) => a.isDefault) || list[0] || null;
        setSelectedAddress(def);
      } catch (err) {
        console.error("Address fetch error:", err);
      } finally {
        setAddressesLoading(false);
      }
    };

    fetchMasterData();
    fetchUserAddresses();
  }, []);

  // Auto-select category, sub-category, product, variety from query parameters if present
  useEffect(() => {
    if (!dataLoading && categories.length > 0) {
      if (paramVarId && productVarieties.length > 0) {
        const foundVar = productVarieties.find((v) => v._id === paramVarId);
        if (foundVar) {
          setVariety(foundVar);
          const pId = foundVar.productId?._id || foundVar.productId;
          const foundProd = products.find((p) => p._id === pId || p._id === paramProdId);
          if (foundProd) {
            setProduct(foundProd);
            const sId = foundProd.subCategoryId?._id || foundProd.subCategoryId;
            const foundSub = subCategories.find((s) => s._id === sId || s._id === paramSubId);
            if (foundSub) {
              setSubCategory(foundSub);
              const cId = foundSub.categoryId?._id || foundSub.categoryId;
              const foundCat = categories.find((c) => c._id === cId || c._id === paramCatId);
              if (foundCat) {
                setCategory(foundCat);
              }
            }
          }
          // Advance directly to Step 1 for smooth user flow
          setCurrentStep(1);
        }
      } else if (paramProdId && products.length > 0) {
        const foundProd = products.find((p) => p._id === paramProdId);
        if (foundProd) {
          setProduct(foundProd);
          const sId = foundProd.subCategoryId?._id || foundProd.subCategoryId;
          const foundSub = subCategories.find((s) => s._id === sId || s._id === paramSubId);
          if (foundSub) {
            setSubCategory(foundSub);
            const cId = foundSub.categoryId?._id || foundSub.categoryId;
            const foundCat = categories.find((c) => c._id === cId || c._id === paramCatId);
            if (foundCat) {
              setCategory(foundCat);
            }
          }
        }
      }
    }
  }, [
    dataLoading,
    categories,
    subCategories,
    products,
    productVarieties,
    paramCatId,
    paramSubId,
    paramProdId,
    paramVarId,
  ]);

  // Filtered dropdown options
  const filteredSubCategories = useMemo(() => {
    if (!category) return [];
    return subCategories.filter((item) => {
      const cId = item.categoryId?._id || item.categoryId;
      return cId === category._id;
    });
  }, [subCategories, category]);

  const filteredProducts = useMemo(() => {
    if (!subCategory) return [];
    return products.filter((item) => {
      const sId = item.subCategoryId?._id || item.subCategoryId;
      return sId === subCategory._id;
    });
  }, [products, subCategory]);

  const filteredVarieties = useMemo(() => {
    if (!product) return [];
    return productVarieties.filter((item) => {
      const pId = item.productId?._id || item.productId;
      return pId === product._id;
    });
  }, [productVarieties, product]);

  // Media Handlers
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (images.length >= 3) {
      toast.error("Maximum 3 reference images allowed (2 Product, 1 Packaging)");
      e.target.value = "";
      return;
    }
    const available = 3 - images.length;
    if (files.length > available) {
      toast.error(`Only ${available} image(s) allowed to reach total 3 compulsory images.`);
    }
    setImages((prev) => [...prev, ...files.slice(0, available)]);
    e.target.value = "";
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (videos.length >= 2) {
      toast.error("Maximum 2 reference videos allowed (1 Product, 1 Packaging)");
      e.target.value = "";
      return;
    }
    const available = 2 - videos.length;
    if (files.length > available) {
      toast.error(`Only ${available} video(s) allowed to reach total 2 compulsory videos.`);
    }
    setVideos((prev) => [...prev, ...files.slice(0, available)]);
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    setVideos((prev) => prev.filter((_, i) => i !== index));
  };

  // Navigation & Step Validation
  const handleStepNext = () => {
    if (currentStep === 0) {
      if (!category || !subCategory || !product || !variety) {
        toast.error("Please select Category, Sub-Category, Product, and Variety to proceed");
        return;
      }
      setCurrentStep(1);
      return;
    }

    if (currentStep === 1) {
      if (!quantity || Number(quantity) <= 0) {
        toast.error("Please enter a valid quantity");
        return;
      }
      if (!minPrice || Number(minPrice) <= 0) {
        toast.error("Please enter a valid price per unit");
        return;
      }
      if (
        !packSize.weight ||
        !packSize.dimensions.length ||
        !packSize.dimensions.breadth ||
        !packSize.dimensions.height
      ) {
        toast.error("Please complete all pack size & dimensional measurements");
        return;
      }
      if (isCustomDimensionUnit && !customDimensionUnit.trim()) {
        toast.error("Please enter a custom dimension unit");
        return;
      }
      if (!holdingPeriod.from || !holdingPeriod.to) {
        toast.error("Please select valid holding period dates");
        return;
      }
      if (!selectedAddress) {
        toast.error("Please select a pickup address");
        return;
      }
      setCurrentStep(2);
      return;
    }
  };

  // Submit Flow Start: Upload Media & Prepare Payload
  const handleSubmitFlowStart = async () => {
    if (images.length !== 3) {
      toast.error("Exactly 3 reference images are compulsory (2 Product Photos, 1 Packaging Photo)");
      return;
    }
    if (videos.length !== 2) {
      toast.error("Exactly 2 reference videos are compulsory (1 Product Video, 1 Packaging Video)");
      return;
    }

    try {
      setUploadingMedia(true);
      setLoaderMessage("Uploading reference photos and videos...");

      // Upload Images to S3
      const uploadedImageUrls = [];
      for (const imgFile of images) {
        if (typeof imgFile === "string") {
          uploadedImageUrls.push(imgFile);
        } else {
          const res = await s3Upload(imgFile, "requirements/rqm/images");
          uploadedImageUrls.push(res.url);
        }
      }

      // Upload Videos to S3
      const uploadedVideoUrls = [];
      for (const vidFile of videos) {
        if (typeof vidFile === "string") {
          uploadedVideoUrls.push(vidFile);
        } else {
          const res = await s3Upload(vidFile, "requirements/rqm/videos");
          uploadedVideoUrls.push(res.url);
        }
      }

      const payload = {
        category: category._id,
        subCategory: subCategory._id,
        product: product._id,
        productVariety: variety._id,
        priceAndQuantity: {
          quantity: Number(quantity),
          currency: "INR",
          amount: Number(minPrice),
          standardizedUnit: variety?.standardizedUNIT || "Kg",
        },
        specification: {
          relatedQuestionsAndAnswers: [],
          otherRequirements: specification.split("\n").filter(Boolean),
        },
        referencePhotoAndVideo: {
          images: uploadedImageUrls,
          videos: uploadedVideoUrls,
        },
        packSize: {
          weight: Number(packSize.weight),
          unit: packSize.unit || "kg",
          dimensions: {
            measured: isCustomDimensionUnit ? customDimensionUnit.trim() : (packSize.dimensions.measured || "cm"),
            length: Number(packSize.dimensions.length),
            breadth: Number(packSize.dimensions.breadth),
            height: Number(packSize.dimensions.height),
          },
        },
        holdingPeriod: {
          from: new Date(holdingPeriod.from).toISOString(),
          to: new Date(holdingPeriod.to).toISOString(),
        },
        pickUpAddress: selectedAddress?._id || null,
      };

      setPendingPayload(payload);
      setIsDeclarationAccepted(false);
      setShowDeclarationModal(true);
    } catch (err) {
      console.error("Media upload error:", err);
      toast.error(err?.message || "Failed to upload reference media");
    } finally {
      setUploadingMedia(false);
      setLoaderMessage("");
    }
  };

  // Declaration Save
  const handleDeclarationContinue = async () => {
    if (!isDeclarationAccepted) {
      toast.error("Please accept the terms in the declaration");
      return;
    }

    try {
      setSubmitting(true);
      setLoaderMessage("Saving declaration agreement...");

      const declarationPayload = {
        reason: "RQM Request Submission Declaration",
        declaration_contents: RQM_DECLARATION_POINTS.map((point) => ({
          content: point,
          isChecked: true,
        })),
        e_sign:
          user?.fullName ||
          [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
          "Supplier Declaration Acceptance",
        date: new Date(),
      };

      await axios.post("/declaration", declarationPayload);
      setShowDeclarationModal(false);
      setShowOTPModal(true);
    } catch (err) {
      console.error("Declaration error:", err);
      toast.error(err?.response?.data?.message || "Failed to save declaration acceptance");
    } finally {
      setSubmitting(false);
      setLoaderMessage("");
    }
  };

  // OTP Verified
  const handleOTPVerified = () => {
    setShowOTPModal(false);
    setTimeout(() => {
      setShowFeeModal(true);
    }, 250);
  };

  // Final Submit: RQM Creation + Wallet Debit
  const handleFinalPlatformFeePayment = async () => {
    if (!pendingPayload) return;

    try {
      setSubmitting(true);
      setLoaderMessage("Processing RQM request and platform fee payment...");

      // 1. Create RQM Request
      const rqmRes = await axios.post("/rqm/create", pendingPayload);
      const rqmId = rqmRes?.data?.data?.rqmId;

      if (!rqmRes?.data?.success || !rqmId) {
        toast.error(rqmRes?.data?.message || "Failed to create RQM request");
        setShowFeeModal(false);
        return;
      }

      // 2. Debit Platform Fee from Wallet
      const transactionPayload = {
        category: "platform_fee",
        amount: PLATFORM_FEE,
        description: "RQM Request Submission Fee",
        platformService: "Supplier RQM Request",
        notes: `RQM_ID:${rqmId}`,
      };

      try {
        await axios.post("/transaction/create-debit-transaction", transactionPayload);
        await refreshWallet?.();
      } catch (debitErr) {
        console.error("Wallet debit error, rolling back RQM:", debitErr);
        await axios.delete(`/rqm/${rqmId}/delete`);
        toast.error(
          debitErr?.response?.data?.message ||
          "Could not deduct platform fee. Please ensure your wallet balance is sufficient."
        );
        setShowFeeModal(false);
        return;
      }

      setShowFeeModal(false);
      toast.success("RQM Request submitted successfully!");
      if (rqmId) {
        navigate(`/supplier-rqm-details?rqmId=${rqmId}`);
      } else {
        navigate("/supplier-rqm-requests");
      }
    } catch (err) {
      console.error("Final submit error:", err);
      toast.error(err?.response?.data?.message || "Failed to complete RQM request creation");
    } finally {
      setSubmitting(false);
      setLoaderMessage("");
    }
  };

  if (dataLoading) {
    return <AppLoader fullScreen message="Loading RQM catalog options..." />;
  }

  return (
    <div className="w-full bg-[#F8FAFC] min-h-screen py-8 px-4 md:px-8">
      {/* GLOBAL BLOCKING APPLOADER */}
      {(submitting || uploadingMedia) && (
        <AppLoader blocking message={loaderMessage || "Processing request..."} />
      )}

      <div className="max-w-7xl mx-auto space-y-6">
        {/* HEADER BAR */}
        <div className="flex items-center justify-between bg-white p-5 rounded-2xl border border-gray-200 shadow-2xs">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/supplier-rqm-requests")}
              className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
            >
              <FiArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">
                Post New RQM Request
              </h1>
              <p className="text-xs text-gray-500">
                Provide product specifications, offered quantities and rate for admin verification.
              </p>
            </div>
          </div>

          <span className="text-xs font-bold px-3 py-1 bg-primaryColor/10 text-primaryColor rounded-full">
            Step {currentStep + 1} of 3
          </span>
        </div>

        {/* SELECTED PRODUCT VARIETY BANNER AT TOP */}
        {variety && (
          <div className="bg-gradient-to-r from-primaryColor/10 via-primaryColor/5 to-transparent border-2 border-primaryColor/30 p-4 sm:p-5 rounded-2xl shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl border border-primaryColor/20 bg-white overflow-hidden flex-shrink-0 flex items-center justify-center p-1 shadow-2xs">
                {variety.img ? (
                  <img src={variety.img} alt={variety.varietyName} className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <FiPackage className="text-2xl text-primaryColor" />
                )}
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-primaryColor text-white">
                    Selected Product Variety
                  </span>
                  {category?.name && (
                    <span className="text-xs text-gray-500 font-medium">
                      {category.name} {subCategory?.name ? `> ${subCategory.name}` : ""}
                    </span>
                  )}
                </div>

                <h3 className="text-base sm:text-lg font-extrabold text-gray-900 leading-tight">
                  {product?.name ? `${product.name} - ` : ""}{variety.varietyName}
                </h3>

                <div className="flex items-center gap-2 flex-wrap text-xs text-gray-700">
                  {variety.grade && (
                    <span className="font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-md">
                      Grade: {variety.grade}
                    </span>
                  )}
                  <span className="font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-md">
                    MOQ: {variety.slabAdjustedMOQ || variety.minimum_MOQ || 0} {variety.standardizedUNIT || "Kg"}
                  </span>
                  {variety.gst && (
                    <span className="font-semibold text-gray-700 bg-gray-100 border border-gray-200 px-2.5 py-0.5 rounded-md">
                      GST: {variety.gst}%
                    </span>
                  )}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setCurrentStep(0)}
              className="px-4 py-2 bg-white border border-gray-300 hover:border-primaryColor text-gray-700 hover:text-primaryColor font-bold text-xs rounded-xl shadow-2xs transition self-start sm:self-center shrink-0 flex items-center gap-1.5"
            >
              Change Variety
            </button>
          </div>
        )}

        {/* 3-COLUMN RESPONSIVE LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* LEFT SIDE: LIVE SELECTION DETAILS CARD */}
          <div className="lg:col-span-3 space-y-6 lg:sticky lg:top-20 order-2 lg:order-1">
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-2xs space-y-3 text-xs">
              <h3 className="font-extrabold text-gray-900 border-b border-gray-100 pb-2.5 flex items-center gap-2">
                <FiCheckCircle className="text-primaryColor" /> Live Selection Details
              </h3>

              <div className="space-y-2.5">
                <div className="py-1 border-b border-gray-100">
                  <span className="text-gray-400 text-[10px] font-bold uppercase block">Selected Variety</span>
                  <span className="font-bold text-gray-900 block truncate mt-0.5">
                    {variety ? `${product?.name ? `${product.name} - ` : ""}${variety.varietyName}` : "Not selected"}
                  </span>
                </div>

                <div className="py-1 border-b border-gray-100">
                  <span className="text-gray-400 text-[10px] font-bold uppercase block">Offered Quantity</span>
                  <span className="font-bold text-gray-900 block mt-0.5">
                    {quantity ? `${quantity} ${variety?.standardizedUNIT || "Kg"}` : "—"}
                  </span>
                </div>

                <div className="py-1 border-b border-gray-100">
                  <span className="text-gray-400 text-[10px] font-bold uppercase block">Offered Price</span>
                  <span className="font-bold text-emerald-600 block mt-0.5">
                    {minPrice ? `₹${Number(minPrice).toLocaleString("en-IN")} / ${variety?.standardizedUNIT || "Kg"}` : "—"}
                  </span>
                </div>

                <div className="py-1 border-b border-gray-100">
                  <span className="text-gray-400 text-[10px] font-bold uppercase block">Total Deal Value</span>
                  <span className="font-extrabold text-primaryColor text-sm block mt-0.5">
                    {quantity && minPrice ? `₹${(Number(quantity) * Number(minPrice)).toLocaleString("en-IN")}` : "—"}
                  </span>
                </div>

                <div className="py-1 border-b border-gray-100">
                  <span className="text-gray-400 text-[10px] font-bold uppercase block">Minimum Pack Weight</span>
                  <span className="font-bold text-gray-900 block mt-0.5">
                    {packSize.weight ? `${packSize.weight} ${packSize.unit || "kg"}` : "—"}
                  </span>
                </div>

                <div className="py-1 border-b border-gray-100">
                  <span className="text-gray-400 text-[10px] font-bold uppercase block">Pack Dimensions</span>
                  <span className="font-bold text-gray-900 block mt-0.5">
                    {packSize.dimensions.length && packSize.dimensions.breadth && packSize.dimensions.height
                      ? `${packSize.dimensions.length} x ${packSize.dimensions.breadth} x ${packSize.dimensions.height} ${isCustomDimensionUnit ? customDimensionUnit : packSize.dimensions.measured}`
                      : "—"}
                  </span>
                </div>

                <div className="py-1 border-b border-gray-100">
                  <span className="text-gray-400 text-[10px] font-bold uppercase block">Pickup Location</span>
                  <span className="font-bold text-gray-900 block truncate mt-0.5">
                    {selectedAddress ? `${selectedAddress.addressType || "Office"} (${selectedAddress.city})` : "—"}
                  </span>
                </div>

                <div className="py-1 border-b border-gray-100">
                  <span className="text-gray-400 text-[10px] font-bold uppercase block">Reference Photos</span>
                  <span className={`font-bold block mt-0.5 ${images.length === 3 ? "text-emerald-600" : "text-amber-600"}`}>
                    {images.length}/3 Uploaded
                  </span>
                </div>

                <div className="py-1">
                  <span className="text-gray-400 text-[10px] font-bold uppercase block">Reference Videos</span>
                  <span className={`font-bold block mt-0.5 ${videos.length === 2 ? "text-emerald-600" : "text-amber-600"}`}>
                    {videos.length}/2 Uploaded
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CENTER SIDE: SELECTION FORM (STEPS 0, 1, 2) */}
          <div className="lg:col-span-6 space-y-6 order-1 lg:order-2">

            {/* STEP PROGRESS BAR */}
            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-2xs flex items-center justify-around text-xs font-semibold text-gray-500">
              <div className={`flex items-center gap-2 ${currentStep >= 0 ? "text-primaryColor font-bold" : ""}`}>
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${currentStep >= 0 ? "bg-primaryColor text-white" : "bg-gray-200"
                    }`}
                >
                  1
                </span>
                <span>Product Variety</span>
              </div>
              <FiChevronRight size={16} />
              <div className={`flex items-center gap-2 ${currentStep >= 1 ? "text-primaryColor font-bold" : ""}`}>
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${currentStep >= 1 ? "bg-primaryColor text-white" : "bg-gray-200"
                    }`}
                >
                  2
                </span>
                <span>Quantity & Price</span>
              </div>
              <FiChevronRight size={16} />
              <div className={`flex items-center gap-2 ${currentStep >= 2 ? "text-primaryColor font-bold" : ""}`}>
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${currentStep >= 2 ? "bg-primaryColor text-white" : "bg-gray-200"
                    }`}
                >
                  3
                </span>
                <span>Reference Media</span>
              </div>
            </div>

            {/* STEP 0: CATALOG SELECTION */}
            {currentStep === 0 && (
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-6">
                <h2 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3 flex items-center gap-2">
                  <FiPackage className="text-primaryColor" /> 1. Select Product Variety
                </h2>

                {dataLoading ? (
                  <div className="py-8 text-center">
                    <div className="w-8 h-8 border-3 border-primaryColor border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-xs text-gray-400">Loading catalog...</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* 1. CATEGORIES */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 uppercase mb-2">
                        Select Category *
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {categories.map((item) => (
                          <button
                            key={item._id}
                            type="button"
                            onClick={() => {
                              setCategory(item);
                              setSubCategory(null);
                              setProduct(null);
                              setVariety(null);
                            }}
                            className={`px-4 py-2 rounded-xl text-xs font-semibold border transition ${category?._id === item._id
                              ? "bg-primaryColor text-white border-primaryColor shadow-xs"
                              : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                              }`}
                          >
                            {item.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 2. SUB-CATEGORIES */}
                    {category && (
                      <div className="pt-3 border-t border-gray-100">
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2">
                          Select Sub-Category *
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {filteredSubCategories.map((item) => (
                            <button
                              key={item._id}
                              type="button"
                              onClick={() => {
                                setSubCategory(item);
                                setProduct(null);
                                setVariety(null);
                              }}
                              className={`px-4 py-2 rounded-xl text-xs font-semibold border transition ${subCategory?._id === item._id
                                ? "bg-primaryColor text-white border-primaryColor shadow-xs"
                                : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                }`}
                            >
                              {item.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 3. PRODUCTS */}
                    {subCategory && (
                      <div className="pt-3 border-t border-gray-100">
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2">
                          Select Product *
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {filteredProducts.map((item) => (
                            <button
                              key={item._id}
                              type="button"
                              onClick={() => {
                                setProduct(item);
                                setVariety(null);
                              }}
                              className={`px-4 py-2 rounded-xl text-xs font-semibold border transition ${product?._id === item._id
                                ? "bg-primaryColor text-white border-primaryColor shadow-xs"
                                : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                }`}
                            >
                              {item.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 4. VARIETIES */}
                    {product && (
                      <div className="pt-3 border-t border-gray-100">
                        <label className="block text-xs font-bold text-gray-700 uppercase mb-2">
                          Select Variety *
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {filteredVarieties.map((item) => (
                            <button
                              key={item._id}
                              type="button"
                              onClick={() => setVariety(item)}
                              className={`px-4 py-2 rounded-xl text-xs font-semibold border transition ${variety?._id === item._id
                                ? "bg-primaryColor text-white border-primaryColor shadow-xs"
                                : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                }`}
                            >
                              {item.varietyName} {item.grade ? `(${item.grade})` : ""}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="pt-4 border-t border-gray-100 flex justify-end">
                  <button
                    onClick={handleStepNext}
                    className="bg-primaryColor hover:bg-primaryColor/90 text-white font-semibold text-xs px-6 py-3 rounded-xl shadow-md transition"
                  >
                    Continue to Quantity & Price
                  </button>
                </div>
              </div>
            )}

            {/* STEP 1: QUANTITY, PRICE & ADDRESS */}
            {currentStep === 1 && (
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-6">
                <h2 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3 flex items-center gap-2">
                  <FiDollarSign className="text-primaryColor" /> 2. Quantity, Price & Pickup Location
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-xs">
                  <div>
                    <label className="block font-bold text-gray-700 mb-1">
                      Offered Quantity ({variety?.standardizedUNIT || "Kg"}) *
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 500"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-primaryColor text-sm font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-1">
                      Offered Price per {variety?.standardizedUNIT || "Kg"} (₹) *
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 120"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-primaryColor text-sm font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-1">
                      Minimum Pack Weight *
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        placeholder="e.g. 25"
                        value={packSize.weight}
                        onChange={(e) => setPackSize((prev) => ({ ...prev, weight: e.target.value }))}
                        onWheel={(e) => e.target.blur()}
                        className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-primaryColor text-sm font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <select
                        value={packSize.unit || "kg"}
                        onChange={(e) => setPackSize((prev) => ({ ...prev, unit: e.target.value }))}
                        className="border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-primaryColor text-sm font-semibold bg-gray-50 cursor-pointer"
                      >
                        <option value="kg">kg</option>
                        <option value="gram">gram</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block font-bold text-gray-700">
                        Pack Dimensions ({isCustomDimensionUnit ? (customDimensionUnit || "unit") : (packSize.dimensions.measured || "cm")}) *
                      </label>
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-semibold text-gray-500">Unit:</span>
                        <select
                          value={isCustomDimensionUnit ? "Other" : (packSize.dimensions.measured || "cm")}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "Other") {
                              setIsCustomDimensionUnit(true);
                            } else {
                              setIsCustomDimensionUnit(false);
                              setPackSize((prev) => ({
                                ...prev,
                                dimensions: { ...prev.dimensions, measured: val },
                              }));
                            }
                          }}
                          className="border border-gray-200 rounded-lg px-2 py-1 outline-none focus:border-primaryColor text-xs font-semibold bg-gray-50 cursor-pointer"
                        >
                          <option value="cm">cm (centimeter)</option>
                          <option value="inch">inch</option>
                          <option value="mm">mm (millimeter)</option>
                          <option value="m">m (meter)</option>
                          <option value="ft">ft (feet)</option>
                          <option value="Other">Other (Custom)</option>
                        </select>
                      </div>
                    </div>

                    {isCustomDimensionUnit && (
                      <div className="mb-2">
                        <input
                          type="text"
                          placeholder="Enter custom dimension unit (e.g. yard, px)"
                          value={customDimensionUnit}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCustomDimensionUnit(val);
                            setPackSize((prev) => ({
                              ...prev,
                              dimensions: { ...prev.dimensions, measured: val },
                            }));
                          }}
                          className="w-full border border-gray-200 rounded-xl px-3 py-1.5 outline-none focus:border-primaryColor text-xs font-semibold mb-1"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="number"
                        placeholder={`L (${isCustomDimensionUnit ? (customDimensionUnit || "unit") : (packSize.dimensions.measured || "cm")})`}
                        value={packSize.dimensions.length}
                        onChange={(e) =>
                          setPackSize((prev) => ({
                            ...prev,
                            dimensions: { ...prev.dimensions, length: e.target.value },
                          }))
                        }
                        onWheel={(e) => e.target.blur()}
                        className="border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-primaryColor text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <input
                        type="number"
                        placeholder={`B (${isCustomDimensionUnit ? (customDimensionUnit || "unit") : (packSize.dimensions.measured || "cm")})`}
                        value={packSize.dimensions.breadth}
                        onChange={(e) =>
                          setPackSize((prev) => ({
                            ...prev,
                            dimensions: { ...prev.dimensions, breadth: e.target.value },
                          }))
                        }
                        onWheel={(e) => e.target.blur()}
                        className="border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-primaryColor text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <input
                        type="number"
                        placeholder={`H (${isCustomDimensionUnit ? (customDimensionUnit || "unit") : (packSize.dimensions.measured || "cm")})`}
                        value={packSize.dimensions.height}
                        onChange={(e) =>
                          setPackSize((prev) => ({
                            ...prev,
                            dimensions: { ...prev.dimensions, height: e.target.value },
                          }))
                        }
                        onWheel={(e) => e.target.blur()}
                        className="border border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-primaryColor text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-1">
                      Holding Period From *
                    </label>
                    <input
                      type="date"
                      value={holdingPeriod.from}
                      onChange={(e) => setHoldingPeriod((prev) => ({ ...prev, from: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-primaryColor text-sm"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-1">
                      Holding Period To *
                    </label>
                    <input
                      type="date"
                      value={holdingPeriod.to}
                      onChange={(e) => setHoldingPeriod((prev) => ({ ...prev, to: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:border-primaryColor text-sm"
                    />
                  </div>
                </div>

                {/* PICKUP ADDRESS */}
                <div className="pt-4 border-t border-gray-100">
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-1">
                    <FiMapPin size={14} className="text-primaryColor" /> Select Pickup Address *
                  </label>

                  {addressesLoading ? (
                    <p className="text-xs text-gray-400">Loading saved addresses...</p>
                  ) : addresses.length === 0 ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                      No saved pickup addresses found. Please add a saved address under your Account settings.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {addresses.map((addr) => (
                        <div
                          key={addr._id}
                          onClick={() => setSelectedAddress(addr)}
                          className={`p-3.5 rounded-xl border cursor-pointer transition text-xs space-y-1 ${selectedAddress?._id === addr._id
                            ? "border-primaryColor bg-primaryColor/5 ring-1 ring-primaryColor/20"
                            : "border-gray-200 hover:border-gray-300 bg-white"
                            }`}
                        >
                          <span className="font-bold text-gray-900 block">{addr.addressType || "Office"}</span>
                          <p className="text-gray-600 truncate">{addr.addressLine1}</p>
                          <p className="text-gray-500">
                            {addr.city}, {addr.state} - {addr.pincode}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <button
                    onClick={() => setCurrentStep(0)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs px-5 py-3 rounded-xl transition"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleStepNext}
                    className="bg-primaryColor hover:bg-primaryColor/90 text-white font-semibold text-xs px-6 py-3 rounded-xl shadow-md transition"
                  >
                    Continue to Reference Media
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: MEDIA UPLOADS & SPECIFICATIONS */}
            {currentStep === 2 && (
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-6">
                <h2 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3 flex items-center gap-2">
                  <FiImage className="text-primaryColor" /> 3. Upload Reference Photos & Videos
                </h2>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 space-y-1">
                  <p className="font-bold">Compulsory Media Guidelines:</p>
                  <p>• <strong>3 Images Compulsory</strong>: Product Image 1, Product Image 2, Packaging Image 1</p>
                  <p>• <strong>2 Videos Compulsory</strong>: Product Video 1, Packaging Video 1</p>
                </div>

                {/* IMAGES */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-1">
                      <FiImage size={14} /> Product & Packaging Photos ({images.length}/3 Compulsory)
                    </label>
                    <span className="text-[11px] font-bold text-primaryColor">
                      {images.length >= 3 ? "✓ 3/3 Selected" : `${3 - images.length} more required`}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-3 items-center">
                    {images.map((img, idx) => (
                      <div key={idx} className="relative w-28 h-28 rounded-xl border overflow-hidden group bg-gray-50">
                        <img
                          src={typeof img === "string" ? img : URL.createObjectURL(img)}
                          alt={`Reference ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute bottom-1 left-1 bg-black/60 text-white px-1.5 py-0.5 rounded text-[9px] font-semibold">
                          {idx === 0 ? "Product 1" : idx === 1 ? "Product 2" : "Packaging 1"}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full text-xs shadow-md"
                        >
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                    ))}

                    <label className="w-28 h-28 rounded-xl border-2 border-dashed border-gray-300 hover:border-primaryColor flex flex-col items-center justify-center cursor-pointer text-gray-400 hover:text-primaryColor transition bg-gray-50">
                      <FiUploadCloud size={24} />
                      <span className="text-[10px] font-bold mt-1">+ Upload Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* VIDEOS */}
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-700 uppercase flex items-center gap-1">
                      <FiVideo size={14} /> Product & Packaging Videos ({videos.length}/2 Compulsory)
                    </label>
                    <span className="text-[11px] font-bold text-primaryColor">
                      {videos.length >= 2 ? "✓ 2/2 Selected" : `${2 - videos.length} more required`}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-3 items-center">
                    {videos.map((vid, idx) => (
                      <div key={idx} className="relative w-40 h-28 rounded-xl border bg-black overflow-hidden">
                        <video
                          src={typeof vid === "string" ? vid : URL.createObjectURL(vid)}
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute bottom-1 left-1 bg-black/70 text-white px-1.5 py-0.5 rounded text-[9px] font-semibold">
                          {idx === 0 ? "Product Video" : "Packaging Video"}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeVideo(idx)}
                          className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full text-xs shadow-md z-10"
                        >
                          <FiTrash2 size={12} />
                        </button>
                      </div>
                    ))}

                    <label className="w-40 h-28 rounded-xl border-2 border-dashed border-gray-300 hover:border-primaryColor flex flex-col items-center justify-center cursor-pointer text-gray-400 hover:text-primaryColor transition bg-gray-50">
                      <FiUploadCloud size={24} />
                      <span className="text-[10px] font-bold mt-1">+ Upload Video</span>
                      <input
                        type="file"
                        accept="video/*"
                        multiple
                        onChange={handleVideoChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* SPECIFICATIONS */}
                <div className="pt-4 border-t border-gray-100">
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Additional Requirements & Specifications (Optional)
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Enter any specific lot details, quality standards or requirements..."
                    value={specification}
                    onChange={(e) => setSpecification(e.target.value)}
                    className="w-full border border-gray-200 rounded-xl p-3 text-xs outline-none focus:border-primaryColor"
                  />
                </div>

                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <button
                    onClick={() => setCurrentStep(1)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold text-xs px-5 py-3 rounded-xl transition"
                  >
                    Back
                  </button>

                  <button
                    onClick={handleSubmitFlowStart}
                    disabled={uploadingMedia || submitting}
                    className="bg-primaryColor hover:bg-primaryColor/90 text-white font-bold text-xs px-8 py-3 rounded-xl shadow-md transition disabled:opacity-50"
                  >
                    Review & Submit RQM Request
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT SIDE: REFERENCE STEPS GUIDE */}
          <div className="lg:col-span-3 space-y-6 lg:sticky lg:top-20 order-3">
            <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-2xs space-y-4">
              <div>
                <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2">
                  📘 How to Post an RQM Request
                </h3>
                <p className="text-xs text-gray-500 mt-1">
                  Follow these steps to submit your RQM requirement for review
                </p>
              </div>

              <div className="space-y-4">
                {RQM_HOW_TO_STEPS.map((step, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primaryColor text-white text-xs font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-800">{step.title}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-primaryColor/5 border border-primaryColor/20 rounded-xl p-3 text-xs text-gray-700">
                💡 <strong>Tip:</strong> Accurate pack size dimensions & clear media speed up admin verification.
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-gray-100 text-xs text-gray-500">
                <span className="text-base">🎧</span>
                <span>
                  Need help? Contact support anytime{" "}
                  <a href="tel:+9108062177433" className="text-primaryColor font-bold hover:underline">
                    +9108062177433
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* MODAL 1: DECLARATION MODAL (CENTERED VIA PORTAL) */}
        {showDeclarationModal &&
          createPortal(
            <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
              <div className="relative bg-white max-w-lg w-full rounded-3xl p-6 sm:p-7 shadow-2xl flex flex-col justify-between space-y-4 max-h-[85vh]">
                <div className="flex items-center gap-2 text-primaryColor font-extrabold text-base border-b border-gray-100 pb-3">
                  <FiShield size={20} /> RQM Supplier Declaration
                </div>

                <p className="text-xs text-gray-600 leading-relaxed">
                  Please review and accept the following declaration points before submitting your RQM request:
                </p>

                <div className="space-y-3 bg-gray-50 p-4 rounded-2xl border border-gray-200 text-xs text-gray-700 overflow-y-auto max-h-56">
                  {RQM_DECLARATION_POINTS.map((pt, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <span className="font-bold text-primaryColor flex-shrink-0">{idx + 1}.</span>
                      <span>{pt}</span>
                    </div>
                  ))}
                </div>

                <label className="flex items-center gap-3 cursor-pointer pt-2">
                  <input
                    type="checkbox"
                    checked={isDeclarationAccepted}
                    onChange={(e) => setIsDeclarationAccepted(e.target.checked)}
                    className="w-4 h-4 text-primaryColor accent-primaryColor rounded"
                  />
                  <span className="text-xs font-bold text-gray-800">
                    I have read and agree to all the terms in the RQM Declaration.
                  </span>
                </label>

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowDeclarationModal(false)}
                    className="px-4 py-2.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeclarationContinue}
                    disabled={!isDeclarationAccepted || submitting}
                    className="px-6 py-2.5 text-xs font-bold bg-primaryColor text-white rounded-xl hover:bg-primaryColor/90 transition disabled:opacity-50"
                  >
                    Accept & Proceed
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        {/* MODAL 2: OTP VERIFICATION */}
        <OTPModal
          show={showOTPModal}
          identifier={user?.phone || user?.email}
          onClose={() => setShowOTPModal(false)}
          onVerified={handleOTPVerified}
        />

        {/* MODAL 3: PLATFORM FEE CONFIRMATION */}
        {showFeeModal &&
          createPortal(
            <div
              onClick={() => setShowFeeModal(false)}
              className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="relative bg-white max-w-md w-full rounded-3xl p-6 sm:p-7 shadow-2xl text-center space-y-4"
              >
                {/* Cross Close Button */}
                <button
                  type="button"
                  onClick={() => setShowFeeModal(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-800 flex items-center justify-center transition-colors cursor-pointer"
                  title="Close"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="w-14 h-14 mx-auto rounded-full bg-primaryColor/10 text-primaryColor flex items-center justify-center">
                  <FiLock size={28} />
                </div>

                <h2 className="text-lg font-extrabold text-gray-900">
                  Confirm Platform Fee Payment
                </h2>

                <p className="text-xs text-gray-500 leading-relaxed">
                  A platform fee of <span className="font-extrabold text-gray-900">₹{PLATFORM_FEE}</span> will be debited from your wallet to activate this RQM request.
                </p>

                <div className="bg-gray-50 p-3.5 rounded-2xl border border-gray-200 text-xs flex justify-between items-center">
                  <span className="text-gray-500">Current Wallet Balance:</span>
                  <span className="font-extrabold text-gray-900">
                    ₹{walletBalance !== null ? Math.round(walletBalance).toLocaleString("en-IN") : 0}
                  </span>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    onClick={() => setShowFeeModal(false)}
                    className="w-1/2 py-3 rounded-xl border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleFinalPlatformFeePayment}
                    disabled={submitting}
                    className="w-1/2 py-3 rounded-xl bg-primaryColor text-white text-xs font-bold hover:bg-primaryColor/90 transition shadow-md disabled:opacity-50"
                  >
                    Pay ₹{PLATFORM_FEE} & Submit
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default PostRQMRequest;
